// Appearance bundles: apply a coordinated look in one call, and let users save
// their current look as a reusable bundle. Built-ins live in settings-schema;
// custom ones are persisted (cross-tab) under BUNDLES_KEY via the generic store.
import { isLocked } from "./look-perks.js";
import { offered } from "./seasons.js";
import { BUNDLE_KEYS, BUNDLES_KEY, BUNDLES, THEMES, WALLPAPER_PRESET_MAP } from "./settings-schema.js";
import { getAll, setMany } from "./settings-store.js";
import { readJSON, writeJSON, subscribe } from "./store.js";
import { getWallpaperImage, setWallpaperImageData } from "./wallpaper-image.js";

// Apply a bundle's settings. setMany only writes the keys present, so a bundle
// composes with the user's other settings rather than resetting them.
export function applyBundle(settings) {
	if (settings && typeof settings === "object") setMany(settings);
}

export function getCustomBundles() {
	const v = readJSON(BUNDLES_KEY, []);
	return Array.isArray(v) ? v : [];
}

// Snapshot the current appearance into a named custom bundle.
export function saveCustomBundle(label) {
	const all = getAll();
	const settings = {};
	for (const k of BUNDLE_KEYS) if (all[k] !== undefined) settings[k] = all[k];
	const name = (label || "My look").trim().slice(0, 30) || "My look";
	const bundle = { id: "u" + Date.now().toString(36), label: name, settings };
	writeJSON(BUNDLES_KEY, getCustomBundles().concat(bundle));
	return bundle;
}

export function deleteCustomBundle(id) {
	writeJSON(BUNDLES_KEY, getCustomBundles().filter((b) => b.id !== id));
}

export function subscribeBundles(fn) {
	return subscribe(BUNDLES_KEY, fn);
}

// True when every key the bundle defines already matches the current settings -
// used to highlight the bundle that reflects the user's current look.
export function bundleMatches(settings) {
	// A "custom" wallpaper look can't be reproduced once its uploaded image is gone,
	// so don't report it as the active look in that case.
	if (settings.wallpaper === "custom" && !getWallpaperImage()) return false;
	const all = getAll();
	return Object.keys(settings).every((k) => all[k] === settings[k]);
}

// Snapshot of the current look (the BUNDLE_KEYS settings).
function currentLook() {
	const all = getAll();
	const s = {};
	for (const k of BUNDLE_KEYS) if (all[k] !== undefined) s[k] = all[k];
	return s;
}

// A shareable LOOK file: the appearance settings plus, when the wallpaper is an
// uploaded image, the image itself - so custom wallpapers travel with the look.
export function exportLook() {
	const settings = currentLook();
	const out = { _rv: "look", v: 1, settings };
	if (settings.wallpaper === "custom") { const img = getWallpaperImage(); if (img) out.image = img; }
	return out;
}

// A shared look (a look file, or a Look Code on the lines that have them) can't hand out a seasonal
// theme or wallpaper outside its season: that part is left out and the person's own stays. Someone
// already using it keeps it, same as the pickers. The rest of the look (accent, font...) still applies.
export function seasonGate(s, now = Date.now()) {
	const all = getAll(), out = { ...s };
	if (!offered(THEMES[s.theme], all.theme === s.theme, now)) delete out.theme;
	if (!offered(WALLPAPER_PRESET_MAP[s.wallpaper], all.wallpaper === s.wallpaper, now)) for (const k in out) if (k.startsWith("wallpaper")) delete out[k];
	return out;
}

// Apply a look file. Keeps only known keys; restores the bundled image first so the
// wallpaper renders, and falls back to "none" if a custom look arrived without a
// (valid) image. Returns false if the file isn't a look.
export function applyLook(obj) {
	if (!obj || obj._rv !== "look" || !obj.settings || typeof obj.settings !== "object") return false;
	const s = {};
	for (const k of BUNDLE_KEYS) if (k in obj.settings) s[k] = obj.settings[k];
	if (!Object.keys(s).length) return false;
	if (s.wallpaper === "custom") {
		if (!(typeof obj.image === "string" && setWallpaperImageData(obj.image))) s.wallpaper = "none";
	}
	setMany(seasonGate(s));
	return true;
}

// A built-in look, preferably different from the current one.
export function randomLook() {
	const free = BUNDLES.filter((b) => !isLocked(b) && offered(b));
	const pool = free.filter((b) => !bundleMatches(b.settings));
	const list = pool.length ? pool : free;
	return list[Math.floor(Math.random() * list.length)].settings;
}
